import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/Icon";
import { PageHero } from "@/components/PublicLayout";
import { Reveal } from "@/components/Reveal";
import { CTABanner } from "@/components/CTABanner";
import { useSite, mediaUrl } from "@/context/SiteContext";
import { COMPANY, IMAGES } from "@/lib/data";

const highlights = [
  { icon: "Heart", title: "Passenger-Centered", text: "Every decision starts with the comfort and dignity of the passenger." },
  { icon: "ShieldCheck", title: "Safety", text: "Trained drivers and well-maintained vehicles on every trip." },
  { icon: "Clock", title: "Reliability", text: "Dependable scheduling you and your care team can count on." },
  { icon: "Handshake", title: "Professionalism", text: "Respectful, courteous service from first call to final drop-off." },
  { icon: "HeartHalf", title: "Compassion", text: "We treat every passenger the way we'd want our own family treated." },
  { icon: "UsersThree", title: "Community", text: "Proudly serving individuals, families, and organizations across PA." },
];

export default function About() {
  const content = useSite();
  const careSrc = content.care_image_id ? mediaUrl(content.care_image_id) : IMAGES.care;
  return (
    <>
      <PageHero
        eyebrow="About CareDeserved"
        title="Transportation With Compassion"
        subtitle="CareDeserved was created to provide safe, reliable, and respectful transportation while recognizing that every passenger deserves dignity and compassionate service."
      />

      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-14 lg:grid-cols-2">
            <Reveal>
              <div className="overflow-hidden rounded-3xl shadow-xl">
                <img src={careSrc} alt="Compassionate caregiver assisting an elderly passenger" className="h-[420px] w-full object-cover" loading="lazy" />
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="eyebrow text-brand-purple">Our Mission</p>
              <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                Every passenger deserves care
              </h2>
              <div className="mt-5 space-y-4 text-slate-600 md:text-lg">
                <p>
                  We know that a ride is rarely just a ride. It's the difference between making it to a life-sustaining
                  dialysis appointment or missing it. It's independence, community, and peace of mind for families.
                </p>
                <p>
                  That's why we built CareDeserved around a simple belief: transportation should be safe, dependable, and
                  delivered with genuine compassion. Our drivers are trained not only to operate our vehicles safely, but
                  to treat every passenger with patience, respect, and dignity.
                </p>
              </div>
              <div className="mt-8 rounded-2xl border border-brand-purple/20 bg-brand-purple/5 p-6">
                <p className="font-heading text-2xl font-bold text-brand-purple">{COMPANY.tagline}</p>
                <p className="mt-1 text-sm text-slate-600">It's more than our tagline — it's how we operate every day.</p>
              </div>
            </Reveal>
          </div>

          <div className="mt-20 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {highlights.map((h, i) => (
              <Reveal key={h.title} delay={i * 0.06}>
                <div className="h-full rounded-2xl border border-slate-200 bg-brand-bg p-7">
                  <span className="flex h-13 w-13 items-center justify-center rounded-2xl bg-brand-teal/10 p-3 text-brand-teal">
                    <Icon name={h.icon} size={26} weight="duotone" />
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-slate-900">{h.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{h.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-16 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Link to="/book-a-ride"><Button data-testid="about-book" className="h-13 rounded-full bg-brand-purple px-8 py-3 font-semibold text-white hover:bg-brand-purple2">Book a Ride</Button></Link>
            <Link to="/facilities"><Button data-testid="about-partner" variant="outline" className="h-13 rounded-full border-brand-blue px-8 py-3 font-semibold text-brand-blue hover:bg-brand-blue hover:text-white">Partner With Us</Button></Link>
          </div>
        </div>
      </section>
      <CTABanner />
    </>
  );
}
